<template>
  <v-container fluid pa-0 style="height: 100%">
    <div :id="mapId" style="height: 100%; z-index: 1;"/>

    <vx-layer-list id="layers" :map="map" :layerControl="layerControl"/>
    <vx-file-list id="files" @onViewFile="openFileMap"/>

    <v-overlay :value="isLoading">
      <v-progress-circular color="primary" indeterminate size="100" width="10"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import L from 'leaflet'
import { SimpleMapScreenshoter } from 'leaflet-simple-map-screenshoter'
import parseGeoraster from 'georaster'
import GeoRasterLayer from 'georaster-layer-for-leaflet'
import shp from 'shpjs'
import chroma from 'chroma-js'
import api from '../../api'

import LayerList from './LayerList'
import FileList from './FileList'

export default {
  name: 'Map',

  components: {
    'vx-layer-list': LayerList,
    'vx-file-list': FileList
  },

  props: {
    mapId: String
  },

  data: () => ({
    isLoading: null,
    map: null,
    layerControl: null
  }),

  computed: {
    ...mapGetters('file', [
      'files'
    ])
  },

  methods: {
    ...mapActions('app', [
      'setLoading'
    ]),

    reload () {
      this.map.invalidateSize()
    },

    initMap () {
      if (this.map === null) {
        let Esri_WorldImagery = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
        })

        let Esri_WorldGrayCanvas = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
          maxZoom: 16
        })

        let basemaps = {
          Esri_WorldImagery: Esri_WorldImagery,
          Esri_WorldGrayCanvas: Esri_WorldGrayCanvas
        }

        /* let Stamen_TonerHybrid = L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/toner-hybrid/{z}/{x}/{y}{r}.{ext}', {
          attribution: 'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          subdomains: 'abcd',
          minZoom: 0,
          maxZoom: 20,
          ext: 'png'
        }) */

        let layers = {}

        this.map = L.map(this.mapId, {
          center: [-3.370516, -60.87205],
          minZoom: 0,
          maxZoom: 15,
          zoom: 4,
          layers: [Esri_WorldGrayCanvas]
        })

        this.layerControl = L.control.layers(basemaps, layers, {
          sortLayers: true,
          sortFunction: (layerA, layerB, nameA, nameB) => {
            return layerA.order - layerB.order
          }
        }) // .addTo(this.map)
        new SimpleMapScreenshoter().addTo(this.map)

        var self = this
        this.map.whenReady(() => {
          // console.log('map loaded')
          self.map.invalidateSize()
        })
      }
    },

    loadFileByParam () {
      let idFile = this.$route.query.idFile

      this.openFileMap(idFile)
    },

    openFileMap (idFile) {
      if (idFile) {
        let file = this.files.find(x => x.ID_FICHERO === idFile)

        if (file) {
          if (file.FORMATO === 'tif') {
            // this.loadGeotiffLayer(file)
            this.loadImageLayer(file)
          } else if (file.FORMATO === 'zip') {
            this.loadShapefileLayer(file)
          }
        }
      }
    },

    loadImageLayer (file) {
      this.isLoading = true

      api.file.getFileMapPng(file).then(response => {
        let body = response.data

        let imageUrl = body.base64
        let imageBounds = body.coordinates
        let layer = L.imageOverlay(imageUrl, imageBounds)

        layer.addTo(this.map)
        this.layerControl.addOverlay(layer, file.NOMBRE)
        if (file.NOMBRE.includes('NonForest')) {
          layer.getElement().classList.add('noForestFilter')
        } else if (file.TIPO_FICHERO.CODIGO === 4 || file.NOMBRE.includes('Forest') || file.NOMBRE.includes('treecover')) {
          layer.getElement().classList.add('forestFilter')
        } else if (file.TIPO_FICHERO.CODIGO === 30 ||
                  file.TIPO_FICHERO.CODIGO === 27 ||
                  file.TIPO_FICHERO.CODIGO === 26 ||
                  file.TIPO_FICHERO.CODIGO === 25 ||
                  file.TIPO_FICHERO.CODIGO === 24 ||
                  file.TIPO_FICHERO.CODIGO === 23 ||
                  file.TIPO_FICHERO.CODIGO === 34 ||
                  file.NOMBRE.includes('Deforestation')) {
          layer.getElement().classList.add('deforestationFilter')
        } else if (file.TIPO_FICHERO.CODIGO === 15) {
          layer.getElement().classList.add('waterFilter')
        } else if (file.TIPO_FICHERO.CODIGO === 1 ||
                  file.TIPO_FICHERO.CODIGO === 2 ||
                  file.TIPO_FICHERO.CODIGO === 3 ||
                  file.TIPO_FICHERO.CODIGO === 9 ||
                  file.TIPO_FICHERO.CODIGO === 10 ||
                  file.TIPO_FICHERO.CODIGO === 12 ||
                  file.TIPO_FICHERO.CODIGO === 13 ||
                  file.TIPO_FICHERO.CODIGO === 14 ||
                  file.TIPO_FICHERO.CODIGO === 15 ||
                  file.TIPO_FICHERO.CODIGO === 16 ||
                  file.TIPO_FICHERO.CODIGO === 17 ||
                  file.TIPO_FICHERO.CODIGO === 18 ||
                  file.TIPO_FICHERO.CODIGO === 19) {
          layer.getElement().classList.add('genericFilter')
        } else if (file.TIPO_FICHERO.CODIGO === (5)) {
          layer.getElement().classList.add('ucFilter')
        }
        this.map.fitBounds(layer.getBounds())

        this.isLoading = false
      }).catch(err => {
        console.error(err)
        this.$toastr.e('Error downloading file: ' + err.message)

        this.isLoading = false
      })
    },

    loadGeotiffLayer (file) {
      this.isLoading = true

      api.file.getFileMapTiff(file).then(response => {
        let arrayBuffer = response.data

        let scale = chroma.scale(['black', 'white'])

        parseGeoraster(arrayBuffer).then(georaster => {
          // let noData = georaster.noDataValue
          let min = georaster.mins[0]

          if (min === -3.3999999521443642e+38) { // TODO: quitar????
            min = 0
          }

          const max = georaster.maxs[0]
          // const range = georaster.ranges[0]

          let layer = new GeoRasterLayer({
            georaster: georaster,
            opacity: 0.9,
            pixelValuesToColorFn: values => {
              let pixelValue = values[0] // there's just one band in this raster

              // if there's zero wind, don't return a color
              if (pixelValue === 0) return null

              // scale to 0 - 1 used by chroma
              let scaledPixelValue = (pixelValue - min) / max

              let color = scale(scaledPixelValue).hex()

              return color
            },

            resolution: 256
          })
          layer.addTo(this.map)
          this.layerControl.addOverlay(layer, file.NOMBRE)
          this.map.fitBounds(layer.getBounds())

          this.isLoading = false
        })
      }).catch(err => {
        console.error(err)
        this.$toastr.e('Error downloading file: ' + err.message)

        this.isLoading = false
      })
    },

    loadShapefileLayer (file) { // Muy lento? -> https://github.com/NelsonMinar/vector-river-map/blob/master/clients/lib/TileLayer.d3_geoJSON.js
      this.isLoading = true

      api.file.getFileMapTiff(file).then(response => {
        let arrayBuffer = response.data

        shp(arrayBuffer).then((geojson) => {
          let layer = L.geoJson(geojson)
          layer.addTo(this.map)
          this.layerControl.addOverlay(layer, file.NOMBRE)
          this.map.fitBounds(layer.getBounds())

          this.isLoading = false
        })
      }).catch(err => {
        console.error(err)
        this.$toastr.e('Error downloading file')

        this.isLoading = false
      })
    }
  },

  activated () {
    this.loadFileByParam()
    this.reload()
  },

  mounted () {
    this.initMap()
  }
}
</script>

<style scoped>
  /* #map {
    height: 100%;
    z-index: 1;
  } */

  #layers {
    position: absolute;
    z-index: 2;
    top: 0px;
    right: 0px;
  }

  #files {
    position: absolute;
    z-index: 2;
    top: 0px;
    left: 50px;
  }

  ::v-deep .forestFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 green);
  }

  ::v-deep .noForestFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 rgb(99, 99, 99));
  }

  ::v-deep .deforestationFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 red);
  }
  ::v-deep .waterFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 rgb(44, 118, 255));
  }
  ::v-deep .genericFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 black);
  }
  ::v-deep .ucFilter {
    filter: opacity(.5) invert(1) drop-shadow(0 0 0 orange);
  }
</style>
